import { useDispatch } from 'react-redux';
import {
	Col,
	Alert,
	Spinner,
	CardBody,
	Card,
	CardHeader,
	Row,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import AddEditFuelSupplyModal from './Add_Edit/AddEditFuelSupply';
import DeleteFuelSupplyModal from './Delete/DeleteFuelSupply';
import { getFuelSupplyList } from '../../../slices/fuelSupply/thunk';
import { getFuelSupplyList as getFuelSupplyListApi } from '../../../helpers/establishment/backend_helper';
import AddEditFuelSupplyReceptionModal from './Add_Edit/AddEditFuelSupplyReception';
import FuelSupplyFilesModal from './Modals/FuelSupplyFilesModal';
import SetBalanceFuelSupplyModal from './Add_Edit/SetBalanceFuelSupplyModal';
import { useUser } from '../../../Components/Hooks/useUserHooks';
import { getFuelTankList } from '../../../slices/thunks';
import { Tooltip } from '@mantine/core';

const FuelSupplyList = ({ establishment_id }) => {
	const dispatch = useDispatch();
	const history = useNavigate();
	const currentUser = useUser();

	const [dataFuelSupply, setDataFuelSupply] = useState({});
	const [isEdit, setIsEdit] = useState(false);
	const [list, setList] = useState(null);
	const [refetch, setRefetch] = useState(false);

	const selectorFuelSupply = createSelector(
		state => state.FuelSupply,
		data => ({
			fuelSupplyList: data.fuelSupplyList,
			isLoadingList: data.isLoadingList,
			isFuelSupplySuccess: data.isFuelSupplySuccess,
		})
	);
	const selectorFuelTank = createSelector(
		state => state.FuelTank,
		data => ({
			fuelTankList: data.fuelTankList,
		})
	);

	const toggleRefetch = () => {
		setRefetch(!refetch);
	};

	const { fuelSupplyList, isLoadingList } = useSelector(selectorFuelSupply);
	const { fuelTankList } = useSelector(selectorFuelTank);

	useEffect(() => {
		if (establishment_id) {
			dispatch(
				getFuelSupplyList(
					establishment_id !== 'all' ? { establishment_id } : undefined
				)
			);
			dispatch(
				getFuelTankList(
					establishment_id !== 'all' ? { establishment_id } : undefined
				)
			);
		}
	}, [establishment_id, dispatch, refetch]);

	useEffect(() => {
		const fetchAndSortFuelSupplyList = async () => {
			if (!fuelSupplyList.length || !fuelTankList.length) {
				setList([]);
				return;
			}
			// Encontrar los últimos movimientos para cada estanque de combustible
			const lastMovements = {};
			fuelSupplyList.forEach(movement => {
				const tankId = movement.fuel_tank?._id;
				if (
					!lastMovements[tankId] ||
					new Date(movement.supply_date) >
						new Date(lastMovements[tankId].supply_date)
				) {
					lastMovements[tankId] = movement;
				}
			});

			// Mapear la lista de suministros de combustible para agregar una propiedad 'canBeDeleted'
			const processedList = fuelSupplyList.map(movement => {
				const tankId = movement.fuel_tank?._id;

				// Regla 1: Solo el último movimiento puede ser eliminado
				const isLastMovement = lastMovements[tankId]?._id === movement._id;

				// Regla 2: 'output_transfer' no se puede eliminar
				const isNotOutputTransfer =
					movement.fuel_supply_type?.code !== 'output_transfer';

				// Regla 3: El estanque debe existir en el establecimiento actual
				const tankExistsInEstablishment = fuelTankList.some(
					tank => tank._id === tankId
				);

				return {
					...movement,
					canBeDeleted:
						isLastMovement &&
						isNotOutputTransfer &&
						tankExistsInEstablishment,
				};
			});

			// Ordena la lista y asigna el resultado al estado
			const sortedFuelSupplyList = [...processedList].sort(
				(a, b) => new Date(b.creation_date) - new Date(a.creation_date)
			);
			// Actualiza el estado list
			setList(sortedFuelSupplyList);
		};

		fetchAndSortFuelSupplyList();
	}, [fuelSupplyList, fuelTankList]);

	// Delete FuelSupply
	const [modal, setModal] = useState(false);
	const [modalSetBalance, setModalSetBalance] = useState(false);
	const [modalReception, setModalReception] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	//Tab
	const [activeTab, setActiveTab] = useState('1');
	const toggleTab = tab => {
		if (activeTab !== tab) {
			setActiveTab(tab);
		}
	};

	function handleToggleModal() {
		setModal(!modal);
		toggleTab('1');
		// validation.resetForm();
	}

	function handleToggleSetBalanceModal() {
		setModalSetBalance(!modalSetBalance);
		toggleTab('1');
	}

	function handleToggleModalDelete() {
		setModalDelete(!modalDelete);
	}

	function handleToggleModalReception() {
		setModalReception(!modalReception);
	}

	// Delete Data
	const handleClickDelete = fuelSupply => {
		setDataFuelSupply(fuelSupply);
		setModalDelete(true);
	};

	// Update Data
	const handleClickEdit = fuelSupply => {
		setDataFuelSupply(fuelSupply);
		handleToggleModal();
	};

	const [modalFilesFuelSupply, setModalFilesFuelSupply] = useState(false);
	function handleToggleModalFilesFuelSupply(data) {
		setModalFilesFuelSupply(!modalFilesFuelSupply);
	}

	const handleClickFile = data => {
		setDataFuelSupply(data);
		handleToggleModalFilesFuelSupply();
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'categories',
				columnDefType: 'display',

				width: '3%',
				Cell: cellProps => {
					const fuelSupply = cellProps.row.original;

					return (
						<React.Fragment>
							<div className='d-flex'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className='list-inline-item'>
											<Link
												to='#'
												onClick={e => {
													if (fuelSupply.file_id) {
														handleClickFile(fuelSupply);
													} else {
														e.preventDefault(); // Evita que el enlace sea clickeado
													}
												}}
												title='Ver Documentos'
												aria-disabled={
													fuelSupply.file_id != null
														? 'false'
														: 'true'
												}
												className={
													fuelSupply.file_id == null
														? 'text-muted'
														: ''
												}
											>
												<i className='ri-file-search-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className='list-inline-item'>
											<Link
												to='#'
												className={`remove-item-btn ${
													!fuelSupply.canBeDeleted
														? 'disabled'
														: ''
												}`}
												onClick={e => {
													if (!fuelSupply.canBeDeleted) {
														e.preventDefault();
														return;
													}
													const fuelSupplyData =
														cellProps.row.original;
													handleClickDelete(fuelSupplyData);
												}}
											>
												<i className='ri-delete-bin-line align-bottom me-2'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'supply_date',
				width: 'auto',
				dataType: 'date',
				defaultSort: 'desc',
			},
			{
				header: 'Tipo de Movimiento',
				accessorKey: 'fuel_supply_type.name',
				size: 'auto',
				dataType: 'multi-select',
				Cell: ({ row }) => {
					let status = row.original.fuel_supply_type;
					let bgColor = status?.bgcolor;
					let txColor = status?.txcolor;
					return (
						<div>
							<span
								className={`badge`}
								style={{ backgroundColor: bgColor, color: txColor }}
							>
								{status ? status.name : '-'}
							</span>
						</div>
					);
				},
			},
			{
				header: 'Estanque',
				accessorKey: 'fuel_tank.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Saldo Anterior',
				accessorKey: 'stock',
				width: 'auto',
				dataType: 'separator',
			},
			{
				header: 'Cantidad',
				accessorKey: 'amount',
				width: 'auto',
				dataType: 'separator',
				Cell: ({ row }) => {
					const formattedNumber = row.original.amount
						.toString()
						.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

					switch (row?.original?.fuel_supply_type.direction) {
						case 'IN':
							return (
								<span className='badge bg-success'>
									{formattedNumber}
								</span>
							);
						case 'OUT':
							return (
								<span className='badge bg-danger'>
									{formattedNumber}
								</span>
							);
						case 'IN/OUT':
							return (
								<span className='badge bg-warning'>
									{formattedNumber}
								</span>
							);
						case 'SET':
							return (
								<span className='badge bg-secondary'>
									{formattedNumber}
								</span>
							);
						default:
							return (
								<span className='badge bg-info'>{formattedNumber}</span>
							);
					}
				},
			},
			{
				header: 'Saldo Actual',
				accessorKey: 'balance',
				width: 'auto',
				dataType: 'separator',
			},
			{
				header: 'Unidad',
				accessorKey: 'unit_measurement.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Capacidad Estanque',
				accessorKey: 'fuel_tank.capacity',
				width: 'auto',
				dataType: 'separator',
			},
			{
				header: 'Tipo de Combustible',
				accessorKey: 'fuel_type.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Origen',
				accessorFn: row => {
					if (
						['transfer_in', 'input_transfer'].includes(
							row.fuel_supply_type.code
						)
					) {
						return row.transfer_establishment
							? row.transfer_establishment.name.toString()
							: '';
					} else if (row.fuel_supply_type.code === 'supply') {
						return row.transfer_company?.name
							? row.transfer_company.name.toString()
							: '';
					}
					return '';
				},
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Destino',
				accessorFn: row => {
					return ['transfer_out', 'output_transfer'].includes(
						row.fuel_supply_type.code
					)
						? row.transfer_establishment
							? row.transfer_establishment.name.toString()
							: ''
						: '';
				},
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Establecimiento',
				accessorKey: 'establishment.name',
				width: 'auto',
			},
			{
				header: 'Artefacto Naval',
				accessorKey: 'feeding_barges.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Ciclo Productivo',
				accessorKey: 'productive_cycle.code',
				width: 'auto',
			},

			{ header: 'Observación', accessorKey: 'observation' },
		],
		[handleClickFile, handleClickDelete]
	);

	return (
		<React.Fragment>
			<style>
				{`
					.remove-item-btn.disabled {
						pointer-events: none;
						cursor: default;
					}
					.remove-item-btn.disabled .ri-delete-bin-line::before {
						color: #adb5bd !important;
					}
				`}
			</style>
			<Row>
				<Col lg={12}>
					<Card>
						{/* {establishment_id != 'all' && (
							<CardHeader className='border-0 pb-0'>
								<div className='d-flex align-items-end justify-content-end'>
									{['ADM', 'ABS'].includes(
										currentUser?.user.role_code
									) && (
										<div className='flex-shrink-0'>
											<div className='d-flex flex-wrap gap-2'>
												<button
													className='btn btn-label btn-primary'
													onClick={() => {
														setIsEdit(false);
														setDataFuelSupply({});
														handleToggleSetBalanceModal();
													}}
												>
													<i className={`${Icons.plus} label-icon`}></i>{' '}
													Ajustar Saldo Inicio de Mes
												</button>
											</div>
										</div>
									)}
									<div
										className='flex-shrink-0'
										style={{ marginLeft: '5px' }}
									>
										<div className='d-flex flex-wrap gap-2'>
											<button
												className='btn btn-label btn-primary'
												onClick={() => {
													setIsEdit(false);
													setDataFuelSupply({});
													handleToggleModal();
												}}
											>
												<i className={`${Icons.plus} label-icon`}></i>{' '}
												Cargar Movimiento de Combustible
											</button>
										</div>
									</div>
									<div
										className='flex-shrink-0'
										style={{ marginLeft: '5px' }}
									>
										<div className='d-flex flex-wrap gap-2'>
											<button
												className='btn btn-label btn-primary'
												onClick={() => {
													handleToggleModalReception();
												}}
											>
												<i className={`${Icons.plus} label-icon`}></i>{' '}
												Recepcionar Combustible
											</button>
										</div>
									</div>
								</div>
							</CardHeader>
						)} */}
						<CardBody>
							<ODataGrid
								columns={columns}
								data={list || []}
								isLoadingData={isLoadingList}
								nameFile={'movimiento_de_combustible'}
								clearCacheFunc={() => {
									getFuelSupplyListApi({
										skip: 0,
										limit: 1,
										clear_cache: true,
									}).then(res => {
										if (res) {
											toggleRefetch();
										}
									});
								}}
								customToolbarActions={({ table }) => (
									establishment_id != 'all' && (
										<div className="d-flex justify-content-start gap-2">
											<div
												className='flex-shrink-0'
												style={{ marginLeft: '5px' }}
											>
												<div className='d-flex flex-wrap gap-2'>
													<Tooltip label='Cargar Movimiento de Combustible' position='top' withinPortal>
														<button
															className='btn d-flex align-items-center btn-tooltip-hover gap-1'
															onClick={() => {
																setIsEdit(false);
																setDataFuelSupply({});
																handleToggleModal();
															}}
														>
															<i className='icon-plus icon-tooltop-size'></i>{' '}
															Cargar
														</button>
													</Tooltip>
												</div>
											</div>
											{['ADM', 'ABS'].includes(
												currentUser?.user.role_code
											) && (
												<div className='flex-shrink-0'>
													<div className='d-flex flex-wrap gap-2'>
														<Tooltip label='Ajustar Saldo Inicio de Mes' position='top' withinPortal>
															<button
																className='btn d-flex align-items-center btn-tooltip-hover gap-1'
																onClick={() => {
																	setIsEdit(false);
																	setDataFuelSupply({});
																	handleToggleSetBalanceModal();
																}}
															>
																<i className='icon-plus icon-tooltop-size'></i>{' '}
																Ajustar
															</button>
														</Tooltip>
													</div>
												</div>
											)}

											<div
												className='flex-shrink-0'
												style={{ marginLeft: '5px' }}
											>
												<div className='d-flex flex-wrap gap-2'>
													<Tooltip label='Recepcionar Combustible' position='top' withinPortal>
														<button
															className='btn d-flex align-items-center btn-tooltip-hover gap-1'
															onClick={() => {
																handleToggleModalReception();
															}}
														>
															<i className='icon-plus icon-tooltop-size'></i>{' '}
															Recepcionar
														</button>
													</Tooltip>
												</div>
											</div>
										</div>
									)
								)}
							/>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<AddEditFuelSupplyModal
				establishment_id={establishment_id}
				dataFuelSupply={dataFuelSupply}
				isEdit={isEdit}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>
			<SetBalanceFuelSupplyModal
				establishment_id={establishment_id}
				dataFuelSupply={dataFuelSupply}
				isEdit={isEdit}
				isOpen={modalSetBalance}
				handleToggleModal={handleToggleSetBalanceModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>

			<AddEditFuelSupplyReceptionModal
				establishment_id={establishment_id}
				isOpen={modalReception}
				handleToggleModal={handleToggleModalReception}
			/>

			<FuelSupplyFilesModal
				dataFuelSupply={dataFuelSupply}
				isOpen={modalFilesFuelSupply}
				handleToggleModal={handleToggleModalFilesFuelSupply}
			/>

			<DeleteFuelSupplyModal
				dataFuelSupply={dataFuelSupply}
				isOpen={modalDelete}
				handlerToggleModal={handleToggleModalDelete}
			/>
		</React.Fragment>
	);
};

export default FuelSupplyList;
